// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Check, ClipboardPlus } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useRipple } from "@/hooks/use-ripple";
import { cn } from "@/lib/utils";

const variantStyles = {
  default: "border-border",
  chain: "bg-[#E3F1FF] border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
  store: "bg-[#FFF5D1] border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900",
} as const;

interface KeyChipProps {
  label: string;
  variant?: keyof typeof variantStyles;
  className?: string;
}

export function KeyChip({ label, variant = "default", className }: KeyChipProps) {
  const { t } = useTranslation("back-office");
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useRipple(ref);

  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(label);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast(
      <span>
        {t("common.copiedToClipboard")} <b>{label}</b>
      </span>,
    );
  }

  return (
    <Badge
      ref={ref}
      variant="secondary"
      className={cn(
        "font-mono font-normal gap-1.5 cursor-pointer select-none [&>svg]:size-2.5 relative overflow-hidden",
        variantStyles[variant],
        className,
      )}
      onClick={handleCopy}
    >
      {label}
      {copied ? <Check className="text-emerald-600" /> : <ClipboardPlus className="opacity-50" />}
    </Badge>
  );
}
